import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  DatePicker,
  Select,
  Input,
  message,
  Modal,
  Descriptions,
  Badge,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  HourglassOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import logService from '../../services/logService';

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function JobLogs() {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: null,
    queue_name: null,
    action: null,
    dateRange: null
  });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        status: filters.status,
        queue_name: filters.queue_name,
        action: filters.action
      };

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await logService.getJobLogs(params);

      if (response.success) {
        setLogs(response.data);
        setPagination({
          ...pagination,
          total: response.meta.total
        });
      } else {
        message.error(response.error || '加载失败');
      }
    } catch (error) {
      console.error('Failed to load job logs:', error);
      message.error('加载Job日志失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await logService.getStats({ hours: 24 });

      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await logService.getJobLog(record.id);

      if (response.success) {
        setSelectedLog(response.data);
        setDetailModalVisible(true);
      } else {
        message.error('加载详情失败');
      }
    } catch (error) {
      message.error('加载详情失败');
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      queued: { color: 'default', icon: <HourglassOutlined />, text: '队列中' },
      processing: { color: 'processing', icon: <SyncOutlined spin />, text: '处理中' },
      completed: { color: 'success', icon: <CheckCircleOutlined />, text: '已完成' },
      failed: { color: 'error', icon: <CloseCircleOutlined />, text: '失败' },
      retry: { color: 'warning', icon: <ClockCircleOutlined />, text: '重试中' }
    };

    const config = statusConfig[status] || { color: 'default', icon: null, text: status };

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: '队列名称',
      dataIndex: 'queue_name',
      key: 'queue_name',
      width: 120
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (action) => action || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status)
    },
    {
      title: '尝试次数',
      dataIndex: 'attempt',
      key: 'attempt',
      width: 100,
      render: (attempt, record) => `${attempt}/${record.max_attempts || 5}`
    },
    {
      title: '入队时间',
      dataIndex: 'queued_at',
      key: 'queued_at',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '开始时间',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '完成时间',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '耗时(秒)',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => duration ? Number(duration).toFixed(2) : '-'
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      )
    }
  ];

  return (
    <div>
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Card>
              <Statistic
                title="总数 (24h)"
                value={stats.jobs.total}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="队列中"
                value={stats.jobs.queued}
                valueStyle={{ color: '#999' }}
                prefix={<HourglassOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="处理中"
                value={stats.jobs.processing}
                valueStyle={{ color: '#1890ff' }}
                prefix={<SyncOutlined spin />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="已完成"
                value={stats.jobs.completed}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="失败"
                value={stats.jobs.failed}
                valueStyle={{ color: '#f5222d' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="平均耗时"
                value={stats.avg_duration}
                suffix="秒"
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card
        title="Job执行日志"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadLogs}>
              刷新
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="状态"
            style={{ width: 120 }}
            allowClear
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
          >
            <Option value="queued">队列中</Option>
            <Option value="processing">处理中</Option>
            <Option value="completed">已完成</Option>
            <Option value="failed">失败</Option>
            <Option value="retry">重试中</Option>
          </Select>

          <Select
            placeholder="队列名称"
            style={{ width: 150 }}
            allowClear
            value={filters.queue_name}
            onChange={(value) => setFilters({ ...filters, queue_name: value })}
          >
            <Option value="subscription">subscription</Option>
            <Option value="default">default</Option>
          </Select>

          <Input
            placeholder="操作"
            style={{ width: 150 }}
            allowClear
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            prefix={<SearchOutlined />}
          />

          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
        </Space>

        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            }
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      <Modal
        title="Job执行详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedLog.id}</Descriptions.Item>
            <Descriptions.Item label="队列名称">{selectedLog.queue_name}</Descriptions.Item>
            <Descriptions.Item label="Job类">{selectedLog.job_class}</Descriptions.Item>
            <Descriptions.Item label="操作">{selectedLog.action || '-'}</Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(selectedLog.status)}
            </Descriptions.Item>
            <Descriptions.Item label="尝试次数">
              {selectedLog.attempt} / {selectedLog.max_attempts}
            </Descriptions.Item>
            <Descriptions.Item label="Subscription ID">
              {selectedLog.subscription_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Invoice ID">
              {selectedLog.invoice_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Order Log ID">
              {selectedLog.order_log_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="入队时间">
              {selectedLog.queued_at ? dayjs(selectedLog.queued_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="开始时间">
              {selectedLog.started_at ? dayjs(selectedLog.started_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="完成时间">
              {selectedLog.completed_at ? dayjs(selectedLog.completed_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="失败时间">
              {selectedLog.failed_at ? dayjs(selectedLog.failed_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="耗时">
              {selectedLog.duration ? `${Number(selectedLog.duration).toFixed(2)}秒` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="载荷(Payload)">
              <pre style={{ maxHeight: 200, overflow: 'auto', background: '#f5f5f5', padding: 8 }}>
                {selectedLog.payload ? JSON.stringify(selectedLog.payload, null, 2) : '-'}
              </pre>
            </Descriptions.Item>
            {selectedLog.result_data && (
              <Descriptions.Item label="结果数据">
                <pre style={{ maxHeight: 200, overflow: 'auto', background: '#f5f5f5', padding: 8 }}>
                  {JSON.stringify(selectedLog.result_data, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedLog.result_message && (
              <Descriptions.Item label="结果消息">
                {selectedLog.result_message}
              </Descriptions.Item>
            )}
            {selectedLog.exception && (
              <Descriptions.Item label="异常">
                <pre style={{ maxHeight: 200, overflow: 'auto', background: '#fff1f0', padding: 8, color: '#cf1322' }}>
                  {selectedLog.exception}
                </pre>
              </Descriptions.Item>
            )}
            {selectedLog.stack_trace && (
              <Descriptions.Item label="堆栈跟踪">
                <pre style={{ maxHeight: 300, overflow: 'auto', background: '#f5f5f5', padding: 8, fontSize: 12 }}>
                  {selectedLog.stack_trace}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}
